import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faCogs } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { Box } from 'src/app/models/box';
import { Channel } from 'src/app/models/channel';
import { EmailAddress } from 'src/app/models/email-address';
import { ChannelType } from 'src/app/models/enums/channel-type';
import { ManagementType } from 'src/app/models/enums/management-type';
import { Rights } from 'src/app/models/enums/rights';
import { FolderState } from 'src/app/models/folder-state';
import { IChannel } from 'src/app/models/interfaces/channel.interface';
import { BoxesService } from 'src/app/services/boxes.service';
import { ChannelService } from 'src/app/services/channel.service';
import { EmailAddressesService } from 'src/app/services/emailAddress.service';
import { FoldersStatesService } from 'src/app/services/folders-states.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { ChannelDetailContext } from './channel-detail-context';
import { ChannelDetailComponent } from './channel-detail/channel-detail.component';
import { ChannelFormContext } from './channel-form-context';
import { ChannelListComponent } from './channel-list/channel-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-channel',
    templateUrl: './channel.component.html',
    styleUrls: ['./channel.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, ChannelListComponent, DetailHeaderComponent, ChannelDetailComponent],
})
export class ChannelComponent extends DefaultManagementComponent<IChannel, Channel, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faChannel = faCogs;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;

    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre canaux.
     */
    public defaultContext: ChannelDetailContext = new ChannelDetailContext();

    /**
     * Liste des bannettes de l'organisation courante.
     */
    boxes: Box[];

    /**
     * Liste des adresses de l'organisation courante.
     */
    addresses: EmailAddress[];

    /**
     * Liste des états de dossier de l'organisation courante.
     */
    folderStates: FolderState[];

    /**
     * Liste des canaux.
     */
    channelTypes: ChannelType[];

    /**
     * Liste des types de management
     */
    managementTypes: ManagementType[];

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     *
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param ChannelService : Service métier utilisé pour gérer les clients.
     */
    constructor(
        protected override notificationService: NotificationService,
        private channelService: ChannelService,
        private boxesService: BoxesService,
        private emailAddressesService: EmailAddressesService,
        private folderStatesService: FoldersStatesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ProcessCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ProcessEdit);

        this.loadBoxes();
        this.loadFolderStates();
        //this.loadWorkflows();
        this.loadAddresses();
        this.loadChannels();
        this.loadManagements();
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param Channel : Valeur du client retourné par le formulaire.
     */
    public channelUpdated(channel: Channel): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.channelService.update(this.context.editedItemId, channel).subscribe(
                (x) => {
                    this.itemsList.refresh();
                    this.displayForm(Channel.createFormCopy(x), FormMode.Reading);
                    this.notificationService.displaySuccess(null, 'Canal de réception mis à jour');
                    this.setDisplayMode(DisplayMode.Form);
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour le canal de réception.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.channelService.add(channel).subscribe(
                (x) => {
                    this.itemsList.refresh();
                    this.displayForm(Channel.createFormCopy(x), FormMode.Reading);
                    this.notificationService.displaySuccess(null, 'Canal de réception créé');
                    this.setDisplayMode(DisplayMode.Form);
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer le canal de réception.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un canal de réception.
     * @param channel Canal de réception selectionné.
     */
    public onChannelSelected(channel: Channel) {
        this.spinner.show('main-spinner');
        this.loadChannel(channel.id).subscribe(
            (x) => {
                this.displayForm(Channel.createFormCopy(x), FormMode.Reading);
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le canal de réception." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param channel Canal de réception sélectionné.
     */
    public onChannelDisplayed(channel: Channel) {
        this.spinner.show('main-spinner');
        this.loadChannel(channel.id).subscribe(
            (x) => {
                this.displayForm(Channel.createFormCopy(x), FormMode.Reading);
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le canal de réception." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param channel Canal de réception sélectionné.
     */
    public onChannelEdited(channel: Channel) {
        this.spinner.show('main-spinner');
        // Cas de la mise à jour.
        this.loadChannel(channel.id).subscribe(
            (x) => {
                this.displayForm(Channel.createFormCopy(x), FormMode.Edition);
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer le canal de réception." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param channel Canal de réception sélectionné.
     */
    public onChannelDuplicated(channel: Channel) {
        let duplicatedChannel = Channel.createFormCopy(channel);
        duplicatedChannel.id = null;
        this.displayForm(duplicatedChannel, FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onChannelCreated() {
        this.displayForm(Channel.createFormCopy(new Channel()), FormMode.Creation);
    }

    /**
     * Appellé lors de l'export
     */
    public onchannelsExported() {
        this.createContext();
        this.itemsList.export('Canaux de réception.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onchannelsImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onchannelsHelped() {
        this.itemsList.displayHelpDialog('channels');
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param channel Canal de réception sélectionné.
     */
    public onChannelDeleted(channel: Channel) {
        this.spinner.show('main-spinner');
        // Suppression effective du Canal de réception.
        this.channelService.delete(channel.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Canal de réception supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de supprimer le canal de réception.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Charge les données d'un canal de réception.
     * @param ChannelId : Id du canal de réception à charger.
     */
    public loadChannel(channelId: number): Observable<Channel> {
        return this.channelService.get(channelId);
    }

    /**
     * Charge la liste des bannettes proposés dans la liste déroulante bannette par défaut du formulaire.
     */
    private loadBoxes() {
        this.spinner.show('main-spinner');
        this.boxesService.List(null, null, null, null, 0, 1000).subscribe(
            (x) => {
                this.boxes = x.value;
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de charger la liste des bannettes.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    // /**
    //  * Charge la liste des workflows proposés dans la liste déroulante workflow par défaut du formulaire.
    //  */
    // private loadWorkflows() {
    // 	this.spinner.show('main-spinner');
    // 	this.workflowDefinitionsService.List().subscribe(
    // 		x => {
    // 			this.workflows = x;
    // 			this.spinner.hide('main-spinner');
    // 		},
    // 		(error: HttpError) => {
    // 			this.notificationService.displayError('Erreur', 'Impossible de charger la liste des workflows.' + error.message);
    // 			this.spinner.hide('main-spinner');
    // 		}
    // 	);
    // }

    /**
     * Charge la liste des adresses email proposées dans la liste déroulante adresse mail du formulaire.
     */
    private loadAddresses() {
        this.spinner.show('main-spinner');
        this.emailAddressesService.List(null, null, null, null, 0, 1000).subscribe(
            (x) => {
                this.addresses = x.value;
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de charger la liste des adresses mail.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Charge la liste des workflows proposés dans la liste déroulante workflow par défaut du formulaire.
     */
    private loadFolderStates() {
        this.spinner.show('main-spinner');
        this.folderStatesService.List(null, null, null, null, 0, 1000).subscribe(
            (x) => {
                this.folderStates = x.value;
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de charger la liste des états de dossier.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    private loadChannels() {
        this.channelTypes = [ChannelType.Fax, ChannelType.Email, ChannelType.Sms, ChannelType.Vocal, ChannelType.Chat];
    }

    private loadManagements() {
        this.managementTypes = [ManagementType.Box, ManagementType.Workflow];
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param channel : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(channel: IChannel): ChannelDetailContext {
        let context = this.createContext();
        context.formContext.item = channel;
        context.editedItemId = channel.id;
        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): ChannelDetailContext {
        let context: ChannelDetailContext = new ChannelDetailContext();
        (context.formContext as ChannelFormContext).boxes = this.boxes;
        // (context.formContext as ChannelFormContext).workflows = this.workflows;
        (context.formContext as ChannelFormContext).folderStates = this.folderStates;
        (context.formContext as ChannelFormContext).channelTypes = this.channelTypes;
        (context.formContext as ChannelFormContext).managementTypes = this.managementTypes;
        (context.formContext as ChannelFormContext).addresses = this.addresses;
        return context;
    }
}
